import React, { useState, useEffect } from "react";
import { Box, Dialog, DialogContent, Typography, Button } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Routes, Route } from "react-router-dom";
import TopNavBar from "./TopNavBar";
import SideNavBar from "./SideNavBar";
import Alerts from "./Alerts";
import Dashboard from "./Dashboard";
import ChatWidget from "./Dashboard/Chat/ChatWidget";
import VideoCall from "./Dashboard/Chat/VideoCall";
import Settings from "./Settings";
import useVersionCheck from "../../hooks/useVersionCheck";
import { GetUser, Logout, setPushAlert, onSocketStatus } from "../../api";
import { socket } from "../../api";

const drawerWidth = 240;

const theme = createTheme({
  palette: {
    mode: "dark",
  },
  typography: {
    button: {
      fontWeight: 400
    },
    h1: {
      fontWeight: 100
    },
    h2: {
      fontWeight: 300
    },
    h3: {
      fontWeight: 300
    },
    h4: {
      fontWeight: 300
    },
    h5: {
      fontWeight: 300
    },
    h6: {
      fontWeight: 300
    },
  },
})

const Portal = () => {
  const [user, setUser] = useState()
  const [access, setAccess] = useState()
  const [alerts, setAlerts] = useState([])
  const [socketStatus, setSocketStatus] = useState()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [globalIncomingCall, setGlobalIncomingCall] = useState(null)
  const [pendingAnsweredCall, setPendingAnsweredCall] = useState(null)
  const [callDeclined, setCallDeclined] = useState(null)
  const [hasSchedule, setHasSchedule] = useState(false)
  const [hasModalitySchedule, setHasModalitySchedule] = useState(false)
  const updateAvailable = useVersionCheck()



  const pushAlertCallback = alert => setAlerts(a => [...a, alert])
  const socketStatusCallback = status => setSocketStatus(status)

  useEffect(() => {
    pushAlertCallback && setPushAlert(pushAlertCallback)
    socketStatusCallback && onSocketStatus(socketStatusCallback)
    return () => { setPushAlert(); onSocketStatus() }
  }, [])

  // Global video call handling
  useEffect(() => {
    if (!socket) return;
    
    const handleIncomingCall = (data) => {
      if (data.fromId !== user?.id) {
        setGlobalIncomingCall(data);
      }
    };
    
    const handleCallEnded = () => {
      setGlobalIncomingCall(null);
    };
    
    const handleCallDeclined = (data) => {
      setCallDeclined(data);
    };
    
    socket.on('video_call_ring', handleIncomingCall);
    socket.on('video_call_ended', handleCallEnded);
    socket.on('video_call_declined', handleCallDeclined);
    
    return () => {
      socket.off('video_call_ring', handleIncomingCall);
      socket.off('video_call_ended', handleCallEnded);
      socket.off('video_call_declined', handleCallDeclined);
    };
  }, [user?.id]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    GetUser().then(resp => {
      if (!resp) Logout()

      const savedOrg = localStorage?.getItem('PortalOrg') || resp.org?.id
      if (!resp.access?.length)
        resp.access = [{ clinic: false, tele: false, org: { id: 0, name: '[No Organization Assigned]' } }]

      if (resp.access.length > 1)
        resp.access.sort((a, b) => {
          const c = a.org.name.toUpperCase()
          const d = b.org.name.toUpperCase()
          if (c < d) return -1
          if (c > d) return 1
          return 0
        })
      setUser(resp)
      setAccess((savedOrg && resp.access.find(e => savedOrg === e.org.id)) || resp.access[0])

    }).catch(e => Logout())
  }, [])

  useEffect(() => {
    if (access && access.org.id !== 0)
        localStorage.setItem('PortalOrg', access.org.id)
  }, [access])

  useEffect(() => {
    setHasSchedule(!!(access?.org.settings?.enableScheduling || access?.org.settings?.scheduler))
    setHasModalitySchedule(!!access?.org.settings?.modalityScheduler)
  }, [access])

  if (user && access)
    return (
      <ThemeProvider theme={theme}>
        <TopNavBar handleDrawerToggle={handleDrawerToggle} user={user} access={access} setAccess={setAccess} />
        <Alerts alerts={alerts} setAlerts={setAlerts} updateAvailable={updateAvailable} />
        <Box sx={{ display: "flex" }}>
          <SideNavBar
            handleDrawerToggle={handleDrawerToggle}
            mobileOpen={mobileOpen}
            drawerWidth={drawerWidth}
            user={user}
            access={access}
            hasSchedule={hasSchedule}
            hasModalitySchedule={hasModalitySchedule}
          />
          <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
            <Routes>
              <Route path="/settings/*" element={<Settings user={user} setUser={setUser} access={access} hasSchedule={hasSchedule} hasModalitySchedule={hasModalitySchedule} />} />
              <Route path="*" element={<Dashboard user={user} access={access} socketStatus={socketStatus} hasSchedule={hasSchedule} hasModalitySchedule={hasModalitySchedule} />} />
            </Routes>
          </Box>
          {!user.settings?.disableChat && <ChatWidget user={user} access={access} socketStatus={socketStatus} />}
        </Box>
        
        {/* Global incoming call dialog */}
        <Dialog open={!!globalIncomingCall} onClose={() => setGlobalIncomingCall(null)}>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Incoming video call from {globalIncomingCall?.from}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => {
                  setPendingAnsweredCall(globalIncomingCall);
                  setGlobalIncomingCall(null);
                }}
              >
                Answer
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => {
                  // Notify caller that call was declined
                  if (socket) {
                    socket.emit('video_call_declined', { 
                      to: globalIncomingCall.fromId
                    });
                  }
                  setGlobalIncomingCall(null);
                }}
              >
                Decline
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        
        {/* Global Video Call Component for answered calls */}
        <VideoCall 
          socket={socket}
          conversationId={null}
          conversation={null}
          user={user}
          pendingAnsweredCall={pendingAnsweredCall}
          onAnsweredCallHandled={() => setPendingAnsweredCall(null)}
          onTranscriptReady={() => {}}
        />
        
        {/* Call declined dialog */}
        <Dialog open={!!callDeclined} onClose={() => setCallDeclined(null)}>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              📞 Call declined
            </Typography>
            <Typography variant="body2">
              Your call was declined.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={() => setCallDeclined(null)}
              >
                OK
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    )

  return <React.Fragment />;
};

export default Portal;
